
import Vue, { VueConstructor } from 'vue'
import { createNamespacedHelpers } from 'vuex'
const { mapActions, mapGetters } = createNamespacedHelpers('link')
const { mapActions: mapNotificationActions } = createNamespacedHelpers('notification')

import { isAlreadyPending } from '@/store/root'
import { SharedLinkListItem } from '@/store/types/link'
import CT from '@/mixins/CopyText'

import {
  ItemListing,
  AvailableTools,
  ItemListingToolbox,
  ItemListingElement,
} from '@/components/listing/'
import { ConfirmLinkDeleteModal } from '@/components/modals'
import { RCheckbox, RIcon, RTooltip, RPill, ChevronBack } from '@/components/'

import { milisecondsToDateString } from '@/services/date'
import { getSharedLinkURL } from '@/services/url'
import { sortByNumericProperty } from '@/services/sorting'
import { openInNewTab } from '@/services/routing'

/**
 * For operations we only need link hashes, so they constitute our state
 * Unfortunately, the changes in Set (or Map) are not trackable by Vue.js 2
 */
type LinksListingsData = {
  selectedHashes: string[]
}

export default (Vue as VueConstructor<Vue & InstanceType<typeof CT>>).extend({
  name: 'SharedLinksView',
  components: {
    ItemListing,
    ItemListingElement,
    ItemListingToolbox,
    RCheckbox,
    RIcon,
    ConfirmLinkDeleteModal,
    RTooltip,
    RPill,
    ChevronBack,
  },
  mixins: [CT],
  data(): LinksListingsData {
    return {
      selectedHashes: [],
    }
  },
  created() {
    this.listLinks()
  },
  methods: {
    selectLink(hash: string) {
      if (this.isHashSelected(hash)) {
        this.deselectLink(hash)
      } else {
        this.selectedHashes.push(hash)
      }
    },
    deselectLink(hash: string) {
      this.selectedHashes = this.selectedHashes.filter(
        (selectedHash: string) => selectedHash !== hash,
      )
    },
    unshareLinks() {
      for (const link in this.selectedHashes) {
        this.unshareLink(link)
      }
    },
    isHashSelected(hash: string): boolean {
      return this.selectedHashes.includes(hash)
    },
    handleSelectAll() {
      if (this.allSelected) {
        this.selectedHashes = []
      } else {
        this.selectedHashes = this.shared.map((link: SharedLinkListItem) => link.hash)
      }
    },

    async handleDelete() {
      const hash = this.selectedHashes[0] // we assume there is only one
      await this.unshareLink(hash)
      this.deselectLink(hash)
      this.$rModal.hide('confirmLinkDeleteModal')
      await this.listLinks()
    },

    handleCopy(hash: string): void {
      const text = getSharedLinkURL(hash)
      this.copyText(text)
      this.success('Link URL copied to the clipboard!')
    },

    showLink(hash: string): void {
      const link = getSharedLinkURL(hash)
      openInNewTab(link)
    },

    // timestamp -> readable date
    date(timestamp: number): string {
      return milisecondsToDateString(timestamp)
    },
    ...mapActions(['shareLink', 'unshareLink', 'listLinks']),
    ...mapNotificationActions(['success']),
  },
  computed: {
    ...mapGetters(['shared', 'listLinksRequest', 'unshareLinkRequest']),
    tools(): AvailableTools {
      const isOneItemSelected = this.selectedHashes.length === 1
      return {
        delete: isOneItemSelected,
        copy: isOneItemSelected,
      }
    },
    linksSortedByCreated(): SharedLinkListItem[] {
      return sortByNumericProperty(this.shared, 'created')
    },
    isListRequestPending(): boolean {
      return isAlreadyPending(this.listLinksRequest)
    },
    isUnsharePending(): boolean {
      return isAlreadyPending(this.unshareLinkRequest)
    },
    allSelected(): boolean {
      return this.selectedHashes.length === this.shared.length
    },
    // needed by the cofirmLinkDelete modal
    modalData(): { title?: string; hash: string } {
      if (!this.selectedHashes.length) return { title: '', hash: '' }
      // get the currently selected link's title and hash
      const { title, hash } = this.shared.find(
        (link: SharedLinkListItem) => link.hash === this.selectedHashes[0],
      )
      return {
        title,
        hash,
      }
    },
  },
})
